import React, { useState } from "react";
import { Folder, FolderPlus, House } from "lucide-react";
import { toast } from "sonner";

import { BrandButton } from "@/shared/components/ui-kit";
import { inputCls } from "@/shared/components/task-atoms";
import { confirmDangerCls, confirmDialogCls } from "@/shared/components/settings-atoms";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/shared/components/ui/dialog";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { useDeleteKbNode, usePatchKbNode } from "@/shared/api/knowledge";
import type { KbTree } from "./hooks/useTree";

import type { KbNode } from "@/shared/types";

export function DeleteNodeDialog({ node, fileCount, onClose }: { node: KbNode; fileCount: number; onClose: () => void }) {
  const remove = useDeleteKbNode();
  const detail =
    node.kind === "folder"
      ? `Deletes the folder and the ${fileCount} ${fileCount === 1 ? "file" : "files"} in it.`
      : "Deletes the file. Agents stop citing it.";

  return (
    <AlertDialog open onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent className={`${confirmDialogCls} max-w-[420px]`}>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[15px] tracking-[-0.01em]">Delete {node.name}?</AlertDialogTitle>
          <AlertDialogDescription className="text-[13px] leading-[1.6]">{detail}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full text-[13px]">Cancel</AlertDialogCancel>
          <button
            className={confirmDangerCls}
            disabled={remove.isPending}
            onClick={() =>
              remove.mutate({ kbId: node.kb_id, nodeId: node.id }, { onSuccess: onClose, onError: (e) => toast.error(e.message) })
            }
          >
            {remove.isPending ? "Deleting…" : "Delete"}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* Folders only, indented by depth. A folder cannot be moved into its own
   subtree, so those rows are left out rather than offered and refused. */
export function MoveNodeDialog({ node, tree, onClose }: { node: KbNode; tree: KbTree; onClose: () => void }) {
  const patch = usePatchKbNode();
  const [target, setTarget] = useState<string | null>(node.parent_id);
  const blocked = node.kind === "folder" ? tree.subtreeIds(node.id) : new Set<string>();

  const rows: { id: string | null; name: string; depth: number }[] = [{ id: null, name: "Top level", depth: 0 }];
  const walk = (parentId: string | null, depth: number) => {
    for (const c of tree.childrenOf(parentId)) {
      if (c.kind !== "folder" || blocked.has(c.id)) continue;
      rows.push({ id: c.id, name: c.name, depth });
      walk(c.id, depth + 1);
    }
  };
  walk(null, 1);

  const move = () =>
    patch.mutate(
      { kbId: node.kb_id, nodeId: node.id, body: { parent_id: target } },
      { onSuccess: onClose, onError: (e) => toast.error(e.message) },
    );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[400px] p-0 gap-0 rounded-[16px] bg-[var(--canvas)] border-[var(--hairline-strong)] shadow-dialog overflow-hidden">
        <div className="px-5 pt-5 pb-4 border-b hairline">
          <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">Move {node.name}</DialogTitle>
          <DialogDescription className="text-[12.5px] text-[var(--text-tertiary)] mt-1">Pick the folder it should live in.</DialogDescription>
        </div>
        <div className="px-2 py-2 max-h-[280px] overflow-y-auto" role="radiogroup" aria-label="Destination folder">
          {rows.map((r) => (
            <button
              key={r.id ?? "root"}
              role="radio"
              aria-checked={target === r.id}
              onClick={() => setTarget(r.id)}
              style={{ paddingLeft: 12 + r.depth * 16 }}
              className={`press w-full flex items-center gap-2 h-8 pr-3 rounded-[8px] text-left text-[12.5px] text-[var(--text-primary)] ${target === r.id ? "bg-[var(--accent-active)]" : "hover:bg-[var(--accent-hover)]"}`}
            >
              {r.id === null ? <House className="w-3.5 h-3.5 text-[var(--text-tertiary)]" strokeWidth={1.75} /> : <Folder className="w-3.5 h-3.5 text-[var(--text-tertiary)]" strokeWidth={1.75} />}
              <span title={r.name} className="truncate">{r.name}</span>
            </button>
          ))}
        </div>
        <div className="px-5 py-3.5 border-t hairline flex items-center justify-end gap-2">
          <button onClick={onClose} className="press h-8 px-3.5 rounded-[8px] text-[13px] font-medium text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]">
            Cancel
          </button>
          <BrandButton onClick={move} disabled={target === node.parent_id || patch.isPending} className="disabled:opacity-40 disabled:pointer-events-none">
            Move
          </BrandButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function NewFolderDialog({ open, onOpenChange, onCreate, description, placeholder = "e.g. Competitor research" }: {
  open?: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (name: string) => void;
  description?: React.ReactNode;
  placeholder?: string;
}) {
  const [name, setName] = useState("");
  const close = () => { setName(""); onOpenChange(false); };
  const create = () => {
    const v = name.trim();
    if (!v) return;
    onCreate(v);
    close();
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(v); }}>
      <DialogContent className="max-w-[400px] p-0 gap-0 rounded-[16px] bg-[var(--canvas)] border-[var(--hairline-strong)] shadow-dialog overflow-hidden">
        <div className="px-5 pt-5 pb-4 border-b hairline">
          <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)] flex items-center gap-2">
            <FolderPlus className="w-4 h-4 text-[var(--text-tertiary)]" strokeWidth={1.75} />
            New folder
          </DialogTitle>
          <DialogDescription className="text-[12.5px] text-[var(--text-tertiary)] mt-1">
            {description}
          </DialogDescription>
        </div>
        <div className="px-5 py-4">
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") create(); }}
            placeholder={placeholder}
            className={inputCls}
          />
        </div>
        <div className="px-5 py-3.5 border-t hairline flex items-center justify-end gap-2">
          <button
            onClick={close}
            className="press h-8 px-3.5 rounded-[8px] text-[13px] font-medium text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]"
          >
            Cancel
          </button>
          <BrandButton
            onClick={create}
            disabled={!name.trim()}
            className="disabled:opacity-40 disabled:pointer-events-none"
          >
            Create folder
          </BrandButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
